import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppSelector } from "Redux/hooks";
import { useRef, useState } from "react";
import { Module, ModuleMap } from "types/type.module";
import RichTextReader from "./RichTextReader";
import { selectStyle } from "Redux/appSlice";

type Step = { step: string; iterated: boolean; hidden: boolean };

type Props = {
  selectedSteps?: Step[];
  module?: Module;
  currentStep?: number;
  onQuestionClick: (index: number) => void;
  moduleMap?: ModuleMap;
};

/*
  @functions TableOfContent
  render table of content based on current steps and module
  @params selectedSteps {array} - current steps
  @params module {object} - module containing steps
  @params current {number} - current step
  @params onQuestionClick {function} - function called when question is clicked
  @params moduleMape {object} - map of Module
  @returns {React.Node}
*/

export default function TableOfContent({
  selectedSteps = [],
  module,
  currentStep = 0,
  onQuestionClick,
  moduleMap,
}: Props) {
  let currentChapter = -1;
  const questions = useRef<HTMLDivElement[]>([]);

  if (!moduleMap || !module) return null;

  const toRender = selectedSteps.map((step, i) => {
    if (step.step !== "toPrint") {
      const pos = Object.values(moduleMap.questions[step.step]);
      let newChapter = <></>;
      if (currentChapter !== pos[0]) {
        currentChapter = pos[0];
        newChapter = (
          <div className=" mt-2 mb-4" id="chapter">
            {module.chapters[currentChapter].name}
          </div>
        );
      }

      return (
        <div key={i}>
          {newChapter}
          <Content
            moduleMap={moduleMap}
            module={module}
            currentStep={currentStep}
            step={step}
            i={i}
            questions={questions}
            onQuestionClick={onQuestionClick}
          />
        </div>
      );
    }
    return (
      <div key={i}>
        <Content
          moduleMap={moduleMap}
          module={module}
          currentStep={currentStep}
          step={step}
          i={i}
          questions={questions}
          onQuestionClick={onQuestionClick}
          toPrint={true}
        />
      </div>
    );
  });
  return <div className="h-[80vh] overflow-y-auto pt-6">{toRender}</div>;
}

type ContentProps = {
  moduleMap: ModuleMap;
  module: Module;
  currentStep?: number;
  step: Step;
  questions: React.MutableRefObject<HTMLDivElement[]>;
  i: number;
  onQuestionClick: (index: number) => void;
  toPrint?: boolean;
};

function Content({
  moduleMap,
  module,
  currentStep,
  step,
  questions,
  i,
  onQuestionClick,
  toPrint = false,
}: ContentProps) {
  const { color1, color2, color3 } = useAppSelector(selectStyle);
  const [color, setColor] = useState(color2);
  const pos = toPrint ? null : Object.values(moduleMap.questions[step.step]);

  return (
    <div
      className={`-my-3 flex flex-row overflow-hidden  text-ellipsis ${
        currentStep !== i ? "cursor-pointer" : "cursor-default"
      }
      ${
        pos && pos[0] !== undefined
          ? module.chapters[pos[0]].questions[pos[1]].conclusion
            ? "pl-4 italic "
            : ""
          : ""
      }
`}
      id="question"
    >
      {currentStep === i && (
        <div className="mr-2">
          <FontAwesomeIcon size="xs" icon={faCircle} />
        </div>
      )}
      {questions.current && (
        <div
          className={`h-8 truncate ${currentStep !== i && "ml-5"}`}
          ref={(el) => {
            if (el) {
              questions.current[i] = el;
            }
          }}
          onClick={() => {
            if (currentStep !== i) {
              onQuestionClick(i);
            }
          }}
          onMouseOver={() => {
            setColor(color1);
          }}
          onMouseOut={() => {
            setColor(color3);
          }}
        >
          <div className="-my-4 ">
            {toPrint || pos === null ? (
              <div style={{ color: color2 }} className="my-4 font-semibold">
                FINAL REPORT
              </div>
            ) : (
              <RichTextReader
                text={
                  // @ts-ignore
                  JSON.parse(module.chapters[pos[0]].questions[pos[1]].content)
                    .text
                }
                justText={true}
                textColor={currentStep === i ? "rgb(38, 47, 61)" : color2}
                size={"1em"}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
